<template>
  <div class="p-6">
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
        <i class="pi pi-box" style="color: var(--primary-gold);"></i>
        <span style="color: var(--text-primary);">Examples & Use Cases</span>
      </h3>
      <p class="text-sm" style="color: var(--text-secondary);">
        Practical examples and real-world scenarios demonstrating framework concepts.
      </p>
    </div>

    <!-- Examples content -->
    <div class="examples-content space-y-4">
      <div class="example-card p-4 rounded-lg border" style="background: var(--background-primary); border-color: var(--border-light);">
        <div class="flex items-start gap-3">
          <div class="p-2 rounded" style="background: var(--background-secondary);">
            <i class="pi pi-lightbulb text-xl" style="color: var(--primary-gold);"></i>
          </div>
          <div class="flex-1">
            <h4 class="font-semibold mb-2" style="color: var(--text-primary);">Academic Medical Center</h4>
            <p class="text-sm mb-2" style="color: var(--text-secondary);">
              Large research hospital implementing AI for clinical decision support across multiple specialties.
            </p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Multi-specialty
              </span>
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Research-focused
              </span>
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Complex governance
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="example-card p-4 rounded-lg border" style="background: var(--background-primary); border-color: var(--border-light);">
        <div class="flex items-start gap-3">
          <div class="p-2 rounded" style="background: var(--background-secondary);">
            <i class="pi pi-building text-xl" style="color: var(--primary-gold);"></i>
          </div>
          <div class="flex-1">
            <h4 class="font-semibold mb-2" style="color: var(--text-primary);">Community Health System</h4>
            <p class="text-sm mb-2" style="color: var(--text-secondary);">
              Regional hospital network deploying AI for patient risk stratification and resource optimization.
            </p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Resource-optimized
              </span>
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Community-focused
              </span>
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Scaled governance
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="example-card p-4 rounded-lg border" style="background: var(--background-primary); border-color: var(--border-light);">
        <div class="flex items-start gap-3">
          <div class="p-2 rounded" style="background: var(--background-secondary);">
            <i class="pi pi-users text-xl" style="color: var(--primary-gold);"></i>
          </div>
          <div class="flex-1">
            <h4 class="font-semibold mb-2" style="color: var(--text-primary);">Specialty Practice Group</h4>
            <p class="text-sm mb-2" style="color: var(--text-secondary);">
              Radiology practice implementing AI-assisted diagnostic tools for imaging interpretation.
            </p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Domain-specific
              </span>
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Workflow-integrated
              </span>
              <span class="px-2 py-1 text-xs rounded" style="background: var(--background-secondary); color: var(--text-secondary);">
                Professional alignment
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional note -->
      <div class="p-4 rounded-lg" style="background: var(--background-secondary);">
        <div class="flex items-start gap-3">
          <i class="pi pi-info-circle text-xl" style="color: var(--primary-gold);"></i>
          <div>
            <p class="text-sm" style="color: var(--text-secondary);">
              For more detailed examples and use case scenarios, refer to the
              <strong style="color: var(--text-primary);">"Who Is It For?"</strong> section in the framework navigation.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Section } from '@/types/ashmatics';

defineProps<{
  section: Section | null;
}>();
</script>

<style scoped>
.example-card {
  transition: all 0.2s ease;
}

.example-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: var(--primary-gold);
}
</style>
